import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchQRCode } from "../../redux/slices/QRCodeSlice";
import QRCode from "react-qr-code";
import {
  ContentCopy,
  Download,
  Info,
  Smartphone,
  Restaurant,
  MenuBook,
  Security,
  QrCode2,
  Link as LinkIcon,
  Share,
} from "@mui/icons-material";
import {
  Box,
  Typography,
  Button,
  Paper
} from "@mui/material";

const DigitalMenuQR = () => {
  const dispatch = useDispatch();
  const { data, loading, error } = useSelector((state) => state.qrcode);
  const { user } = useSelector((state) => state.user);

  const [copied, setCopied] = useState(false);
  const [qrSize, setQrSize] = useState(220);
  const qrRef = useRef(null);

  useEffect(() => {
    dispatch(fetchQRCode());
  }, [dispatch]);

  const copyLink = () => {
    if (data?.url) {
      navigator.clipboard.writeText(data.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

    const downloadQR = async () => {
  if (!qrRef.current || !data?.url) return;

  const svg = qrRef.current.querySelector("svg");
  if (!svg) return;

  const serializer = new XMLSerializer();
  const svgStr = serializer.serializeToString(svg);

  // SVG → Blob (Unicode safe)
  const svgBlob = new Blob([svgStr], {
    type: "image/svg+xml;charset=utf-8",
  });
  const url = URL.createObjectURL(svgBlob);

  const img = new Image();

  img.onload = () => {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    const qrSize = 300;
    const padding = 40;
    const width = 400;
    const height = 520;
    
    canvas.width = width;
    canvas.height = height;

    // Background
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, width, height);

    // Header
    const gradient = ctx.createLinearGradient(0, 0, width, 0);
    gradient.addColorStop(0, "#0b4064");
    gradient.addColorStop(1, "#1e88e5");
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, width, 90);

    ctx.fillStyle = "#fff";
    ctx.font = "bold 20px Arial";
    ctx.textAlign = "center";
    ctx.fillText(
      user?.restaurantName || "Restaurant Digital Menu",
      width / 2,
      35
    );

    ctx.font = "14px Arial";
    ctx.fillText("Scan to View Menu & Place Order", width / 2, 65);

    // QR draw FIRST
    const qrX = (width - qrSize) / 2;
    const qrY = 120;
    ctx.drawImage(img, qrX, qrY, qrSize, qrSize);

    // Logo circle on QR
    const logoSize = 60;
    ctx.fillStyle = "#fff";
    ctx.beginPath();
    ctx.arc(
      width / 2,
      qrY + qrSize / 2,
      logoSize / 2,
      0,
      Math.PI * 2
    );
    ctx.fill();
    ctx.strokeStyle = "#0b4064";
    ctx.lineWidth = 2;
    ctx.stroke();

    // Logo text
    const initials =
      user?.restaurantName
        ?.split(" ")
        .map((w) => w[0])
        .join("")
        .substring(0, 2)
        .toUpperCase() || "R";

    ctx.fillStyle = "#0b4064";
    ctx.font = "bold 20px Arial";
    ctx.textBaseline = "middle";
    ctx.fillText(initials, width / 2, qrY + qrSize / 2);

    // Footer
    ctx.fillStyle = "#333";
    ctx.font = "14px Arial";
    ctx.fillText(
      `Owner: ${user?.ownerName || ""}`,
      width / 2,
      qrY + qrSize + 50
    );

    // if (user?.mobile) {
    //   ctx.font = "13px Arial";
    //   ctx.fillText(
    //     `Contact: ${user.mobile}`,
    //     width / 2,
    //     qrY + qrSize + 75
    //   );
    // }

    // Download
    const png = canvas.toDataURL("image/png");
    const link = document.createElement("a");
    link.href = png;
    link.download = `${
      user?.restaurantName?.replace(/\s+/g, "-") || "restaurant"
    }-qr.png`;
    link.click();

    URL.revokeObjectURL(url);
  };

  img.src = url;
};

  const shareQRCode = async () => {
    if (navigator.share && data?.url) {
      try {
        await navigator.share({
          title: `${user?.restaurantName || "Restaurant"} - Digital Menu`,
          text: `Scan QR code to view ${user?.restaurantName || "our"}'s menu and order online! Owner: ${user?.ownerName || ""}`,
          url: data.url,
        });
      } catch (err) {
        console.log("Share failed:", err);
      }
    }
  };

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.spinner}></div>
        <p style={styles.loadingText}>Generating Digital Menu QR Code...</p>
      </div>
    );
  }

  if (error) {
    return (
      <Box sx={styles.errorContainer}>
        <Typography variant="h6" color="error" gutterBottom>
          Error Loading QR Code
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {error.message || error}
        </Typography>
        <Button
          variant="contained"
          onClick={() => dispatch(fetchQRCode())}
          sx={{ mt: 2 }}
        >
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <>
     <Box style={styles.header}>
          <Box>
            <Typography variant="h4" sx={styles.title}>
              Digital Menu QR Code
            </Typography>
            <Typography sx={styles.subtitle}>
              Generate and share this QR code for your restaurant customers
            </Typography>
          </Box>
        </Box>
  
    <Paper elevation={2} sx={styles.mainContainer}>

      <Box sx={{ maxWidth: 1350, mx: "auto", px: { xs: 2, sm: 3 } }}>
       
        <Box style={styles.content} id="qr-card-design">
          {/* QR Code Section */}
          <Box style={styles.qrSection}>
            <Box style={styles.qrContainer} ref={qrRef}>
              <Box style={styles.qrBackground}>
                {data?.url ? (
                  <QRCode
                    value={data.url}
                    size={qrSize}
                    fgColor="#1a365d"
                    bgColor="transparent"
                  />
                ) : (
                  <Box sx={{ p: 4, textAlign: "center" }}>
                    <QrCode2 sx={{ fontSize: 100, color: "action.disabled" }} />
                    <Typography color="text.secondary">
                      No QR Code Available
                    </Typography>
                  </Box>
                )}
                <Box style={styles.qrOverlay}>
                  <Restaurant sx={{ fontSize: 40, color: "rgba(0,0,0,0.1)" }} />
                </Box>
              </Box>
              <Box style={styles.qrLabel}>Scan to View Menu & Place Order</Box>
            </Box>

            {/* Action Buttons */}
            <Box sx={{ display: "flex", gap: 2, mt: 3, flexWrap: "wrap" }}>
              <Button
                variant="outlined"
                startIcon={<Download />}
                onClick={downloadQR}
                disabled={!data?.url}
                sx={styles.actionButton}
              >
                QR Code
              </Button>

              <Button
                  variant="outlined"
                  startIcon={<Share />}
                  onClick={shareQRCode}
                  disabled={!data?.url}
                  sx={{ flex: 1, minWidth: 150 }}
                >
                  Share
                </Button>
            </Box>
          </Box>

          {/* Link & Information Section */}
          <Box style={styles.infoSection}>
            {/* Direct Link */}
            <Box style={styles.linkSection}>
              <Box style={styles.sectionHeader}>
                <LinkIcon sx={{ fontSize: 20, color: "#0b4064" }} />
                <label style={styles.sectionLabel}>Digital Menu URL</label>
              </Box>
              <Box style={styles.linkContainer}>
                <Box style={styles.linkBox}>
                  <code style={styles.linkText}>
                    {data?.url || "No URL available"}
                  </code>
                </Box>
                <button
                  onClick={copyLink}
                  style={{
                    ...styles.copyButton,
                    background: copied ? "#059669" : "#0b4064",
                  }}
                  disabled={!data?.url}
                >
                  <ContentCopy sx={{ fontSize: 20 }} />
                  {copied ? "Copied!" : "Copy Link"}
                </button>
              </Box>
            </Box>

            {/* Usage Instructions */}
            <Box style={styles.instructions}>
              <Box style={styles.instructionsHeader}>
                <Info sx={{ fontSize: 24, color: "#0c4a6e" }} />
                <h3 style={styles.instructionsTitle}>
                  How to Use This Digital Menu QR Code
                </h3>
              </Box>

              <Box style={styles.featureGrid}>
                <Box style={styles.featureCard}>
                  <Box style={styles.featureIcon}>
                    <Smartphone sx={{ fontSize: 24 }} />
                  </Box>
                  <h4 style={styles.featureTitle}>Share & Display</h4>
                  <p style={styles.featureDescription}>
                    Share via WhatsApp, email, or print for table tops, banners,
                    and entrance displays
                  </p>
                </Box>

                <Box style={styles.featureCard}>
                  <Box style={styles.featureIcon}>
                    <MenuBook sx={{ fontSize: 24 }} />
                  </Box>
                  <h4 style={styles.featureTitle}>Digital Menu</h4>
                  <p style={styles.featureDescription}>
                    Customers can scan, browse complete menu, and place orders
                    directly
                  </p>
                </Box>

                <Box style={styles.featureCard}>
                  <Box style={styles.featureIcon}>
                    <Security sx={{ fontSize: 24 }} />
                  </Box>
                  <h4 style={styles.featureTitle}>Menu Management</h4>
                  <p style={styles.featureDescription}>
                    Control menu items, prices, and availability in real-time
                  </p>
                </Box>
              </Box>

              {/* Management Features */}
              <Box style={styles.managementSection}>
                <h4 style={styles.managementTitle}>
                  Complete Menu Management:
                </h4>
                <Box style={styles.featureList}>
                  <Box style={styles.featureItem}>
                    <span style={styles.checkmark}>✓</span> Update prices and
                    discounts instantly
                  </Box>
                  <Box style={styles.featureItem}>
                    <span style={styles.checkmark}>✓</span> Mark items as
                    available/sold out
                  </Box>
                  <Box style={styles.featureItem}>
                    <span style={styles.checkmark}>✓</span> Categorize dishes
                    (Starters, Main Course, etc.)
                  </Box>
                  <Box style={styles.featureItem}>
                    <span style={styles.checkmark}>✓</span> Add special offers
                    and combos
                  </Box>
                  <Box style={styles.featureItem}>
                    <span style={styles.checkmark}>✓</span> Real-time menu
                    updates
                  </Box>
                  <Box style={styles.featureItem}>
                    <span style={styles.checkmark}>✓</span> Customer reviews and
                    ratings
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Paper>
      </>
  );
};

// Styles
const styles = {
  mainContainer: {
    width: "100%",
    minHeight: "100vh",
    borderRadius:  "10px",
    // background: "#f8fafc",
    py: { xs: 1, md: 2 },
  },
  loadingContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "60px 20px",
  },
  spinner: {
    width: "50px",
    height: "50px",
    border: "4px solid #e2e8f0",
    borderTop: "4px solid #0b4064",
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
  },
  loadingText: {
    marginTop: "16px",
    color: "#64748b",
    fontSize: "16px",
    fontWeight: "500",
  },
  errorContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "60vh",
    p: 3,
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
    marginBottom: "20px",
  },
  headerIcon: {
    background: "linear-gradient(135deg, #0b4064 0%, #1e88e5 100%)",
    padding: "14px",
    borderRadius: "14px",
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  title: {
    margin: "0",
    fontSize: "28px",
    fontWeight: "700",
    color: "#1a365d",
    lineHeight: "1.2",
  },
  subtitle: {
    margin: "4px 0 0 0",
    fontSize: "15px",
    color: "#64748b",
    fontWeight: "400",
  },
  restaurantCard: {
    background: "linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)",
    mb: 4,
    border: "1px solid #bae6fd",
  },
  divider: {
    height: "1px",
    background: "linear-gradient(90deg, transparent, #e2e8f0, transparent)",
    margin: "24px 0",
  },
  content: {
    display: "flex",
    gap: "32px",
    flexWrap: "wrap",
    alignItems: "flex-start",
  },
  qrSection: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "20px",
    minWidth: "280px",
  },
  qrContainer: {
    textAlign: "center",
  },
  qrBackground: {
    background: "white",
    padding: "24px",
    borderRadius: "16px",
    boxShadow: "0 4px 20px rgba(11, 64, 100, 0.1)",
    border: "1px solid #e2e8f0",
    marginBottom: "12px",
    position: "relative",
  },
  qrOverlay: {
    position: "absolute",
    top: "0",
    left: "0",
    right: "0",
    bottom: "0",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    pointerEvents: "none",
  },
  qrLabel: {
    fontSize: "14px",
    color: "#64748b",
    fontWeight: "500",
    marginTop: "8px",
  },
  actionButton: {
    flex: 1,
    minWidth: 180,
  },
  infoSection: {
    flex: "1",
    minWidth: "300px",
  },
  linkSection: {
    marginBottom: "28px",
  },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    marginBottom: "12px",
  },
  sectionLabel: {
    fontSize: "14px",
    color: "#475569",
    fontWeight: "600",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  linkContainer: {
    display: "flex",
    gap: "12px",
    flexWrap: "wrap",
  },
  linkBox: {
    flex: "1",
    padding: "14px 18px",
    fontSize: "14px",
    borderRadius: "10px",
    border: "1px solid #cbd5e1",
    background: "#f8fafc",
    overflow: "hidden",
    minWidth: "200px",
  },
  linkText: {
    color: "#1e293b",
    fontFamily: "'Roboto Mono', monospace",
    wordBreak: "break-all",
  },
  copyButton: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    padding: "14px 24px",
    borderRadius: "10px",
    border: "none",
    background: "#0b4064",
    color: "white",
    cursor: "pointer",
    fontSize: "15px",
    fontWeight: "600",
    transition: "all 0.3s ease",
    minWidth: "140px",
    boxShadow: "0 4px 12px rgba(11, 64, 100, 0.2)",
    "&:hover": {
      background: "#0d6efd",
    },
    "&:disabled": {
      background: "#cbd5e1",
      cursor: "not-allowed",
    },
  },
  instructions: {
    background: "linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)",
    padding: "24px",
    borderRadius: "16px",
    border: "1px solid #bae6fd",
  },
  instructionsHeader: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    marginBottom: "20px",
  },
  instructionsTitle: {
    margin: "0",
    fontSize: "18px",
    fontWeight: "700",
    color: "#0c4a6e",
  },
  featureGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: "16px",
    marginBottom: "24px",
  },
  featureCard: {
    background: "white",
    padding: "20px",
    borderRadius: "12px",
    border: "1px solid #e2e8f0",
    transition: "transform 0.2s ease, box-shadow 0.2s ease",
    "&:hover": {
      transform: "translateY(-4px)",
      boxShadow: "0 8px 25px rgba(11, 64, 100, 0.1)",
    },
  },
  featureIcon: {
    width: "48px",
    height: "48px",
    borderRadius: "12px",
    background: "linear-gradient(135deg, #0b4064 0%, #1e88e5 100%)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "white",
    marginBottom: "16px",
  },
  featureTitle: {
    margin: "0 0 8px 0",
    fontSize: "16px",
    fontWeight: "600",
    color: "#1a365d",
  },
  featureDescription: {
    margin: "0",
    fontSize: "14px",
    color: "#475569",
    lineHeight: "1.5",
  },
  managementSection: {
    marginTop: "20px",
    paddingTop: "20px",
    borderTop: "1px dashed #cbd5e1",
  },
  managementTitle: {
    margin: "0 0 16px 0",
    fontSize: "16px",
    fontWeight: "600",
    color: "#1a365d",
  },
  featureList: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: "10px",
  },
  featureItem: {
    fontSize: "14px",
    color: "#475569",
    padding: "10px 14px",
    background: "rgba(255, 255, 255, 0.7)",
    borderRadius: "8px",
    border: "1px solid #e2e8f0",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  checkmark: {
    color: "#059669",
    fontWeight: "bold",
    fontSize: "16px",
  },
  benefitCard: {
    height: "100%",
    transition: "transform 0.2s",
    "&:hover": {
      transform: "translateY(-4px)",
    },
  },
};

// Add Grid import at top

// Add animation
const addSpinnerAnimation = () => {
  if (typeof document !== "undefined") {
    const style = document.createElement("style");
    style.textContent = `
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `;
    document.head.appendChild(style);
  }
};

addSpinnerAnimation();

export default DigitalMenuQR;